import React, { lazy, Suspense, useContext, useMemo, useState } from "react";
import "./Quiz.css";
import { SettingsContext } from "../../MathFactsGenerator/Settings/SettingsContext.tsx";
import { useCanPreviewPdf } from "../../Shared/Pdf/useCanPreviewPdf.ts";
import { worksheetEventParams } from "../../MathFactsGenerator/worksheetEventParams.ts";
import { newSeed } from "../../Shared/Helpers.tsx";
import { pagesFromFacts } from "../../MathFactsGenerator/ProblemGenerator.tsx";
import { readMissedFacts } from "./missedFacts.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

// Behind the lazy boundary like every other PDF surface — the renderer only
// loads if a child actually asks for a practice sheet.
const WorksheetPreview = lazy(
    () => import("../../MathFactsGenerator/PdfGenerator/WorksheetPreview.tsx")
);

/**
 * "12 facts worth another look" and a button to print a practice sheet built
 * from them (ADR 0012 §5). Renders nothing when the store is empty — a visitor
 * who has taken no quiz has no misses, and a dead control is worse than none.
 *
 * `refreshKey` lets the quiz bump this after a run so a freshly finished quiz's
 * misses show up without a reload.
 */
export function MissedFactsPractice({ refreshKey = 0 }: { refreshKey?: number }) {
    const { settings } = useContext(SettingsContext);
    const canPreview = useCanPreviewPdf();
    const [seed] = useState(() => newSeed());
    const [showSheet, setShowSheet] = useState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const facts = useMemo(() => readMissedFacts(), [refreshKey]);
    const t = useTranslation();

    if (facts.length === 0) {
        return null;
    }

    const sheets = showSheet
        ? [{ settings, seed, footerSeed: seed, pageData: pagesFromFacts(facts, settings) }]
        : null;

    return (
        <div className="missedFactsPractice">
            <p className="missedFactsCount">
                {format(t('flashCards.missedFactsCountTemplate'), {
                    count: facts.length,
                    factWord: facts.length === 1 ? t('flashCards.fact') : t('flashCards.facts'),
                })}
            </p>
            {!showSheet ? (
                <button
                    type="button"
                    className="missedFactsPrint"
                    onClick={() => setShowSheet(true)}
                >
                    {t('flashCards.printForPractice')}
                </button>
            ) : (
                <Suspense fallback={<div className="pdfLoading">{t('flashCards.loadingPracticeSheet')}</div>}>
                    <WorksheetPreview
                        settings={settings}
                        seed={seed}
                        sheets={sheets}
                        canPreview={canPreview}
                        fileName="practice-worksheet.pdf"
                        eventParams={{
                            ...worksheetEventParams(settings),
                            // Distinguishes a sheet printed from the missed-facts
                            // panel (ADR 0012) from one built on a generator page.
                            source: 'missed_facts',
                        }}
                    />
                </Suspense>
            )}
        </div>
    );
}

export default MissedFactsPractice;
